<template>
  <div class="document-mission">
    <div class="document-mission__grid">
      <div class="document-mission__main">
        <section class="mission-panel">
          <div class="mission-panel__header">
            <span class="mission-panel__header-icon" aria-hidden="true">
              <i class="uil-file-edit-alt"></i>
            </span>
            <h5 class="mission-panel__title">{{ $t('general.task_description') }}</h5>
          </div>
          <textarea
            :value="missionDescription"
            class="mission-panel__textarea"
            name="short_description"
            rows="5"
            :placeholder="$t('general.task_description')"
            @input="$emit('update:missionDescription', $event.target.value)"
          ></textarea>
        </section>

        <AnswerTypeSelector
          :accepted_right_answer="acceptedRightAnswer"
          :completion_rule="completionRule"
          :isAnswerTypeMission="answerType"
          @update:answerType="$emit('update:answerType', $event)"
          @update:completion_rule="$emit('update:completionRule', $event)"
          @update:accepted_right_answer="$emit('update:acceptedRightAnswer', $event)"
        />
      </div>

      <aside class="document-mission__side">
        <section class="mission-panel mission-panel--file">
          <div class="mission-panel__header">
            <span class="mission-panel__header-icon" aria-hidden="true">
              <i class="uil-cloud-upload"></i>
            </span>
            <h5 class="mission-panel__title">{{ $t('general.mission_file') }}</h5>
          </div>

          <label class="mission-toggle">
            <span class="uk-switch mb-0">
              <input
                :checked="shouldUploadMissionFile"
                type="checkbox"
                @change="$emit('update:shouldUploadMissionFile', $event.target.checked)"
              />
              <div class="uk-switch-slider uk-switch-small"></div>
            </span>
            <span class="mission-toggle__label">{{ $t('general.load_task_file') }}</span>
          </label>

          <div v-if="shouldUploadMissionFile" class="mission-panel__body">
            <label
              class="mission-dropzone"
              :class="{
                'mission-dropzone--dragging': isDragging,
                'mission-dropzone--has-file': !!missionFilePreviewUrl,
              }"
              for="uploadMissionFile"
            >
              <input
                id="uploadMissionFile"
                :accept="acceptableFileType"
                class="mission-dropzone__input"
                draggable="true"
                type="file"
                @change="handleFileChange"
                @dragenter.prevent="isDragging = true"
                @dragleave.prevent="isDragging = false"
                @dragover.prevent
                @drop.prevent="onDrop"
              />
              <i class="uil-cloud-upload mission-dropzone__icon" aria-hidden="true"></i>
              <span class="mission-dropzone__title">{{ $t('general.upload_file') }}</span>
              <span class="mission-dropzone__hint">
                {{ $t("general.or") }} {{ $t('general.drag') }}
              </span>
            </label>

            <div v-if="missionFilePreviewUrl" class="mission-file-card">
              <div class="mission-file-card__icon" aria-hidden="true">
                <i :class="fileTypeIcon"></i>
              </div>
              <div class="mission-file-card__content">
                <div class="mission-file-card__name" :title="missionFileDisplayName">
                  {{ missionFileDisplayName }}
                </div>
                <div class="mission-file-card__meta">
                  <span class="mission-file-card__badge">{{ missionFileExtension }}</span>
                </div>
              </div>
              <button
                type="button"
                class="btn btn-sm btn-primary mission-file-card__action"
                :disabled="isProcessingFile"
                @click="viewMissionFile"
              >
                <i :class="viewButtonIcon"></i>
                <span>{{ viewButtonLabel }}</span>
              </button>
            </div>

            <div v-if="showInlinePreview && canPreviewInline" class="mission-file-preview-media">
              <img
                v-if="missionFilePreviewType === 'image'"
                :src="missionFilePreviewUrl"
                :alt="$t('general.mission_file')"
                class="mission-file-preview-media__image"
                @error="onInlineImageError"
              />

              <iframe
                v-else-if="missionFilePreviewType === 'pdf'"
                :src="missionFilePreviewUrl"
                :title="$t('general.mission_file')"
                class="mission-file-preview-media__pdf"
                frameborder="0"
              />
            </div>
          </div>

          <p v-else class="mission-panel__empty">
            {{ $t('general.load_task_file') }}
          </p>
        </section>
      </aside>
    </div>
    <div id="previewMission"></div>
  </div>
</template>

<script>
import AnswerTypeSelector from "@/view/company/Content/components/AnswerTypeSelector.vue";

const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"];
const PDF_EXTENSIONS = ["pdf"];

export default {
  name: "DocumentMission",
  components: {
    AnswerTypeSelector,
  },
  props: {
    missionDescription: {
      type: String,
      default: "",
    },
    answerType: {
      type: String,
      default: "file",
    },
    completionRule: {
      type: String,
      default: "answered",
    },
    acceptedRightAnswer: {
      type: String,
      default: "",
    },
    shouldUploadMissionFile: {
      type: Boolean,
      default: false,
    },
    missionFile: {
      type: [String, File, Object],
      default: null,
    },
    acceptableFileType: {
      type: String,
      default: "*",
    },
    icon: {
      type: String,
      default: "icon-feather-book-open",
    },
  },
  data() {
    return {
      isDragging: false,
      localPreviewUrl: null,
      showInlinePreview: false,
      isProcessingFile: false,
      inlineImageFailed: false,
    };
  },
  computed: {
    isRemoteMissionFile() {
      return typeof this.missionFile === "string" && !!this.missionFile;
    },
    isLocalMissionFile() {
      return this.missionFile instanceof File;
    },
    missionFilePreviewUrl() {
      if (this.isRemoteMissionFile) return this.missionFile;
      if (this.isLocalMissionFile) return this.localPreviewUrl;
      return null;
    },
    missionFileName() {
      if (this.isLocalMissionFile) return this.missionFile.name || "mission-file";
      if (!this.isRemoteMissionFile) return "-";
      try {
        const pathname = new URL(this.missionFile, window.location.origin).pathname;
        const rawName = decodeURIComponent(pathname.split("/").pop() || "");
        return rawName || "mission-file";
      } catch (e) {
        return "mission-file";
      }
    },
    missionFileDisplayName() {
      if (this.isLocalMissionFile) return this.missionFile.name || "-";
      if (this.isRemoteMissionFile) return this.missionFileName || "-";
      return "-";
    },
    fileExtension() {
      const name = (this.missionFileName || "").toLowerCase();
      const match = name.match(/\.([a-z0-9]+)(?:\?|$)/i);
      return match ? match[1] : "";
    },
    missionFileExtension() {
      if (this.fileExtension) return this.fileExtension.toUpperCase();
      if (this.missionFilePreviewType === "pdf") return "PDF";
      if (this.missionFilePreviewType === "image") return "IMAGE";
      return "-";
    },
    missionFilePreviewType() {
      if (this.inlineImageFailed) return "document";

      if (this.fileExtension) {
        if (IMAGE_EXTENSIONS.includes(this.fileExtension)) return "image";
        if (PDF_EXTENSIONS.includes(this.fileExtension)) return "pdf";
        return "document";
      }

      if (this.isLocalMissionFile) {
        const type = (this.missionFile.type || "").toLowerCase();
        if (type.startsWith("image/")) return "image";
        if (type.includes("pdf")) return "pdf";
        return "document";
      }

      if (this.isRemoteMissionFile) {
        const lowerUrl = String(this.missionFile).toLowerCase();
        if (lowerUrl.includes("/images/") || lowerUrl.includes("/image/")) return "image";
        if (lowerUrl.includes("/pdf/") || lowerUrl.includes("/pdfs/")) return "pdf";
      }

      return "document";
    },
    canPreviewInline() {
      return ["image", "pdf"].includes(this.missionFilePreviewType);
    },
    viewButtonLabel() {
      if (this.isProcessingFile) {
        return this.$t("general.downloading") || this.$t("general.download");
      }
      return this.$t("general.view");
    },
    viewButtonIcon() {
      return this.canPreviewInline ? "uil-eye" : "uil-external-link-alt";
    },
    fileTypeIcon() {
      if (this.missionFilePreviewType === "image") return "uil-image";
      if (this.missionFilePreviewType === "pdf") return "uil-file-alt";
      if (this.fileExtension === "json") return "uil-file-graph";
      if (["ppt", "pptx"].includes(this.fileExtension)) return "uil-presentation-line";
      if (["doc", "docx"].includes(this.fileExtension)) return "uil-file-contract";
      if (["xls", "xlsx"].includes(this.fileExtension)) return "uil-file-graph";
      return "uil-file";
    },
  },
  watch: {
    missionFile: {
      immediate: true,
      handler(file) {
        this.revokeLocalPreviewUrl();
        this.showInlinePreview = false;
        this.inlineImageFailed = false;
        if (file instanceof File) {
          this.localPreviewUrl = URL.createObjectURL(file);
        } else {
          this.localPreviewUrl = null;
        }
        this.$nextTick(() => {
          if (this.canPreviewInline && this.missionFilePreviewUrl) {
            this.showInlinePreview = true;
          }
        });
      },
    },
  },
  beforeDestroy() {
    this.revokeLocalPreviewUrl();
  },
  methods: {
    revokeLocalPreviewUrl() {
      if (this.localPreviewUrl) {
        URL.revokeObjectURL(this.localPreviewUrl);
        this.localPreviewUrl = null;
      }
    },
    handleFileChange(event) {
      const file = event.target.files?.[0];
      if (file) {
        this.$emit("file-uploaded", file);
        this.isDragging = false;
      }
    },
    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        this.$emit("file-uploaded", file);
      }
    },
    onInlineImageError() {
      this.inlineImageFailed = true;
      this.showInlinePreview = false;
    },
    viewMissionFile() {
      const fileUrl = this.missionFilePreviewUrl;
      if (!fileUrl || this.isProcessingFile) return;

      if (this.canPreviewInline) {
        this.showInlinePreview = true;
        return;
      }

      window.open(fileUrl, "_blank", "noopener,noreferrer");
    },
  },
};
</script>

<style scoped>
.document-mission {
  width: 100%;
}

.document-mission__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(280px, 1fr);
  gap: 16px;
  align-items: start;
}

.document-mission__main,
.document-mission__side {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.mission-panel {
  padding: 16px;
  background: #fff;
  border: 1px solid rgba(62, 65, 109, 0.12);
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(62, 65, 109, 0.04);
}

.mission-panel__header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.mission-panel__header-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: rgba(62, 65, 109, 0.08);
  color: #3e416d;
  flex-shrink: 0;
}

.mission-panel__header-icon i {
  font-size: 16px;
}

.mission-panel__title {
  margin: 0;
  color: #3e416d;
  font-size: 15px;
  font-weight: 700;
}

.mission-panel__textarea {
  display: block;
  width: 100%;
  min-height: 120px;
  padding: 12px 14px;
  border: 1px solid #e7e9f0;
  border-radius: 10px;
  background: #f8f9fb;
  color: #495057;
  font-size: 14px;
  line-height: 1.55;
  resize: vertical;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
}

.mission-panel__textarea:focus {
  outline: none;
  border-color: rgba(62, 65, 109, 0.35);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(62, 65, 109, 0.08);
}

.mission-panel__body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.mission-panel__empty {
  margin: 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f8f9fb;
  border: 1px dashed rgba(62, 65, 109, 0.18);
  color: #9799b1;
  font-size: 13px;
  font-weight: 500;
}

.mission-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  cursor: pointer;
}

.mission-toggle__label {
  color: #495057;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.3;
}

.mission-dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 120px;
  padding: 18px 14px;
  margin: 0;
  border: 1.5px dashed rgba(62, 65, 109, 0.28);
  border-radius: 10px;
  background:
    linear-gradient(180deg, rgba(62, 65, 109, 0.03), rgba(62, 65, 109, 0.01)),
    #f8f9fb;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.mission-dropzone:hover,
.mission-dropzone--dragging {
  border-color: #3e416d;
  background: rgba(62, 65, 109, 0.04);
  box-shadow: inset 0 0 0 1px rgba(62, 65, 109, 0.08);
}

.mission-dropzone--has-file {
  min-height: 88px;
  padding: 14px;
}

.mission-dropzone__input {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}

.mission-dropzone__icon {
  font-size: 28px;
  color: #3e416d;
  line-height: 1;
}

.mission-dropzone__title {
  color: #3e416d;
  font-size: 13px;
  font-weight: 700;
}

.mission-dropzone__hint {
  color: #9799b1;
  font-size: 12px;
  font-weight: 500;
}

.mission-file-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid rgba(62, 65, 109, 0.12);
  border-radius: 10px;
  background: #fff;
}

.mission-file-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  border-radius: 9px;
  background: rgba(62, 65, 109, 0.08);
  color: #3e416d;
}

.mission-file-card__icon i {
  font-size: 18px;
}

.mission-file-card__content {
  min-width: 0;
  flex: 1;
}

.mission-file-card__name {
  color: #3e416d;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mission-file-card__meta {
  margin-top: 4px;
}

.mission-file-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba(62, 65, 109, 0.08);
  color: #3e416d;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.mission-file-card__action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;
}

.mission-file-preview-media {
  overflow: hidden;
  border: 1px solid #eef0f4;
  border-radius: 10px;
  background: #f8f9fb;
}

.mission-file-preview-media__image {
  display: block;
  max-width: 100%;
  max-height: 260px;
  margin: 0 auto;
  object-fit: contain;
}

.mission-file-preview-media__pdf {
  width: 100%;
  min-height: 360px;
  border: 0;
  background: #f8f9fb;
}

@media (max-width: 991px) {
  .document-mission__grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .mission-file-card {
    flex-wrap: wrap;
  }

  .mission-file-card__action {
    width: 100%;
    justify-content: center;
  }
}
</style>
